/**
 * Coerce an upstream Grahak `status` field onto the legacy lowercase string
 * the rest of the app expects (`'success' | 'fail' | ...`).
 *
 * The fallback is **opt-in** — each call site declares whether a missing
 * status should be treated as success or failure. Default is `'fail'` so we
 * fail closed when in doubt; transformers that have already validated the
 * presence of `data.data` (i.e. the upstream payload is well-formed and we
 * just want to project it) explicitly pass `'success'`.
 */
const normalizeStatus = (status: unknown, fallback: 'success' | 'fail' = 'fail'): string =>
  typeof status === 'string' ? status.toLowerCase() : fallback;

/**
 * Upstream Grahak shapes — kept intentionally permissive (every field
 * optional/nullable) because the backend is still evolving and individual
 * transformers fail-soft on missing values rather than rejecting outright.
 */
interface IGrahakUserDetails {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dob?: string | null;
  gender?: string | null;
  userId?: string | number | null;
  mobile?: string | null;
  hideEmail?: boolean | null;
  email?: string | null;
  isEmailVerified?: boolean | null;
}

interface IGrahakSessionDetails {
  accessToken?: string;
  refreshToken?: string;
}

interface IGrahakOtpData {
  authId?: string;
  maxAttemptLimit?: number;
  resendIntervalSec?: number;
  resendCount?: number;
  expiry?: number;
  requestTimestamp?: number;
}

interface IGrahakVerifyData {
  userDetails?: IGrahakUserDetails;
  sessionDetails?: IGrahakSessionDetails;
  cartCount?: number;
  newUser?: boolean;
}

interface IGrahakResponse<TData = unknown> {
  status?: string;
  message?: string;
  errorCode?: string | number;
  data?: TData;
}

/**
 * Legacy (consumer-facing) shapes — what the rest of the app reads after the
 * transformer normalises the upstream payload.
 */
interface ILegacyOtpEnvelope {
  status: string;
  response: {
    message: string;
    otpId: string;
    retryAllowed: number;
    resendAllowed: number;
    resendInterval: number;
    otpValidity: number;
  };
}

interface ILegacyActionOtpEnvelope {
  status: string;
  response: {
    message: string;
    authId: string;
    otpId: string;
    retryAllowed: number;
    resendAllowed: number;
    resendInterval: number;
    otpValidity: number;
  };
}

interface ILegacyVerifyEnvelope {
  status: string;
  message: string;
  response: {
    user: {
      name: string;
      firstname: string | null;
      lastname: string | null;
      dob: string | null;
      gender: string | null;
      userId: number;
      mobile: string;
      hideEmail: boolean;
      newUser: 0 | 1;
      email: string | null;
      isEmailVerified: boolean | null;
      encrypted_customer_id: string;
      cartCount: number;
    };
    wishlist_updated_at: number;
    wishlist_count: number;
    accessToken: string;
    refreshToken: string;
  };
}

interface ILegacyExecuteActionEnvelope {
  status: string;
  message: string;
  response?: unknown;
  errorCode?: string | number;
}

/**
 * Transforms Grahak "send OTP" API response → legacy Nykaa send OTP shape
 */
const transformSendOtpResponse = (
  data: IGrahakResponse<IGrahakOtpData>
): ILegacyOtpEnvelope | IGrahakResponse<IGrahakOtpData> => {
  if (!data?.data) return data;
  const d = data.data;
  return {
    status: normalizeStatus(data.status, 'success'),
    response: {
      message: data.message || 'OTP Sent Successfully',
      otpId: d.authId || '', // Fallback to empty string instead of undefined
      retryAllowed: d.maxAttemptLimit ?? 3,
      resendAllowed: 3,
      resendInterval: d.resendIntervalSec || 30,
      otpValidity:
        d.expiry && d.requestTimestamp ? Math.floor((d.expiry - d.requestTimestamp) / 1000) : 300,
    },
  };
};

/**
 * Transforms Grahak "resend OTP" API response → legacy Nykaa resend OTP shape
 */
const transformResendOtpResponse = (data: IGrahakResponse<IGrahakOtpData>): ILegacyOtpEnvelope => {
  if (!data?.data) {
    return {
      // Normalise the status here too so 'FAIL' / 'Fail' coming from upstream
      // is lowercased before consumers do strict equality checks (status === 'fail').
      status: normalizeStatus(data?.status, 'fail'),
      response: {
        message: data?.message || 'Something went wrong',
        otpId: '',
        retryAllowed: 0,
        resendAllowed: 0,
        resendInterval: 0,
        otpValidity: 0,
      },
    };
  }

  const d = data.data;
  return {
    status: normalizeStatus(data.status, 'success'),
    response: {
      message: data.message || 'OTP Resent Successfully',
      otpId: d.authId || '',
      retryAllowed: d.maxAttemptLimit ?? 3,
      resendAllowed: d.resendCount !== undefined ? 3 - d.resendCount : 3,
      resendInterval: d.resendIntervalSec ?? 30,
      otpValidity:
        d.expiry && d.requestTimestamp ? Math.floor((d.expiry - d.requestTimestamp) / 1000) : 300,
    },
  };
};

/**
 * Transforms Grahak "verify OTP" API response → legacy login verify response
 */
const transformVerifyJwtResponse = (
  res: IGrahakResponse<IGrahakVerifyData> & { response?: { user?: unknown } }
): ILegacyVerifyEnvelope | typeof res => {
  // Already legacy compatible — bail out without re-wrapping.
  const legacyUser = res?.response?.user;
  if (legacyUser) return res;

  const userDetails = res?.data?.userDetails;
  if (userDetails) {
    const { sessionDetails, cartCount, newUser } = res.data || {};
    const {
      fullName = '',
      firstName = null,
      lastName = null,
      dob = null,
      gender = null,
      userId = 0,
      mobile = '',
      hideEmail = true,
      email = null,
      isEmailVerified = null,
    } = userDetails;
    const { accessToken = '', refreshToken = '' } = sessionDetails || {};

    return {
      status: normalizeStatus(res.status, 'success'),
      message: res.message || '',
      response: {
        user: {
          name: fullName || '',
          firstname: firstName,
          lastname: lastName,
          dob,
          gender,
          userId: Number(userId) || 0,
          mobile: mobile || '',
          hideEmail: hideEmail ?? true,
          newUser: newUser ? 1 : 0,
          email: email || null,
          // `||` would replace a legitimate `false` with `null`; `??` keeps
          // the verified-but-explicitly-false case intact.
          isEmailVerified: isEmailVerified ?? null,
          encrypted_customer_id: '',
          cartCount: cartCount ?? 0,
        },
        wishlist_updated_at: 0,
        wishlist_count: 0,
        accessToken,
        refreshToken,
      },
    };
  }

  // Neither legacy nor new shape — surface a normalised failure envelope so
  // consumers can rely on `status` + `message` instead of crashing while
  // dereferencing `response.user`.
  return {
    status: normalizeStatus(res?.status, 'fail'),
    message: res?.message || 'Login verification failed',
    response: {
      user: {
        name: '',
        firstname: null,
        lastname: null,
        dob: null,
        gender: null,
        userId: 0,
        mobile: '',
        hideEmail: true,
        newUser: 0,
        email: null,
        isEmailVerified: null,
        encrypted_customer_id: '',
        cartCount: 0,
      },
      wishlist_updated_at: 0,
      wishlist_count: 0,
      accessToken: '',
      refreshToken: '',
    },
  };
};

/**
 * Transforms Grahak "send action OTP" API response → legacy format
 */
const transformSendActionOtpResponse = (
  data: IGrahakResponse<IGrahakOtpData>
): ILegacyActionOtpEnvelope | IGrahakResponse<IGrahakOtpData> => {
  if (!data?.data) return data;
  const d = data.data;
  return {
    status: normalizeStatus(data.status, 'success'),
    response: {
      message: data.message || 'OTP Sent Successfully',
      authId: d.authId || '',
      otpId: d.authId || '', // Keep for backward compatibility
      retryAllowed: d.maxAttemptLimit || 3,
      resendAllowed: 3,
      resendInterval: d.resendIntervalSec || 30,
      otpValidity:
        d.expiry && d.requestTimestamp ? Math.floor((d.expiry - d.requestTimestamp) / 1000) : 300,
    },
  };
};

/**
 * Transforms Grahak "execute user action" API response → legacy format
 */
const transformExecuteActionResponse = (
  data: IGrahakResponse<unknown>
): ILegacyExecuteActionEnvelope => {
  if (!data?.data) {
    return {
      // Same fix as the resend-OTP error path: lowercase 'FAIL' / 'Fail' /
      // missing-status into the canonical 'fail' string consumers compare against.
      status: normalizeStatus(data?.status, 'fail'),
      message: data?.message || 'Action execution failed',
      errorCode: data?.errorCode,
    };
  }

  return {
    status: normalizeStatus(data.status, 'success'),
    message: data.message || 'Email updated successfully',
    response: data.data,
  };
};

type ResponseType = 'sendOtp' | 'resendOtp' | 'verifyJwt' | 'sendActionOtp' | 'executeAction';

export const normalizeResponse = (type: ResponseType, data: unknown): any => {
  // The transformers themselves are defensive against missing/malformed
  // payloads (every field is optional in the Grahak input interfaces and each
  // transformer guards `data?.data`). Cast through the matching input type at
  // the dispatch boundary so the public API can still accept `unknown` from
  // callers that haven't typed their fetch responses yet.
  switch (type) {
    case 'sendOtp':
      return transformSendOtpResponse(data as IGrahakResponse<IGrahakOtpData>);
    case 'resendOtp':
      return transformResendOtpResponse(data as IGrahakResponse<IGrahakOtpData>);
    case 'verifyJwt':
      return transformVerifyJwtResponse(
        data as IGrahakResponse<IGrahakVerifyData> & { response?: { user?: unknown } }
      );
    case 'sendActionOtp':
      return transformSendActionOtpResponse(data as IGrahakResponse<IGrahakOtpData>);
    case 'executeAction':
      return transformExecuteActionResponse(data as IGrahakResponse<unknown>);
    default:
      return data;
  }
};
